import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { common, cards } from '../helpers/api';
import { PageContainer } from './common';
import Loader from '../components/Loader';
import QuickSearchForm from '../components/forms/QuickSearchForm';
import GirlSmilingImg from '../assets/vrpat/mulher-sorrindo-com-notebook.png';
import devices from '../styles/devices';
import { ToastCtx } from '~/providers/ToastProvider';
import messages from '../helpers/api/messages';
import { closeModal } from '~/store/modules/modal/actions';
import { setProvisionalCard } from '~/store/modules/home/actions';
import { VRPAT } from '~/config/constants';

const QuickSearchContainer = styled(PageContainer)`
  background-color: ${({ theme }) => theme.colors.institucional};
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 30px 0;

  @media (min-width: ${devices?.desktop}) {
    flex-direction: row;
    align-items: center;
    height: calc(100vh - 64px);
    padding: 0 0 0 37px;
  }
`;
const Image = styled.img`
  width: 80%;
  max-width: 565px;
  align-self: flex-start;

  @media (min-width: ${devices.mobile}) {
    width: 90%;
    align-self: flex-start;
  }
`;
const FormContainer = styled.div`
  margin-top: 5px;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;

  @media (min-width: ${devices?.desktop}) {
    background-color: ${({ theme }) => theme.colors.white};
    margin-top: 0;
    width: 50%;
    height: 100%;
  }
`;
const Title = styled.h1`
  position: relative;
  font-size: 1.625rem;
  color: ${({ theme }) => theme.colors.white};
  line-height: 33px;
  font-weight: 700;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    left: -40px;
    top: -100px;
    height: 200px;
    width: 100%;
    z-index: -1;
    ${({ isVR }) =>
      isVR &&
      css`
        background: linear-gradient(
          180deg,
          rgb(255 255 255 / 0%) 0%,
          rgba(0, 170, 19, 1) 100%
        );
      `}
  }

  @media (min-width: ${devices?.desktop}) {
    line-height: 38px;
  }
`;
const Description = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.white};
  max-width: 525px;
  line-height: 36px;
  font-weight: 500;
  margin-left: 40px;
  padding-right: 15px;
  z-index: 1;

  @media (min-width: ${devices?.desktop}) {
    font-weight: 600;
  }
`;
const ContainerImage = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${devices?.desktop}) {
    width: 50%;
  }
`;
const TitleContainer = styled.div`
  padding-left: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${({ isVR }) => (isVR ? '-75px' : '-5px')};
  position: relative;

  &:before {
    content: '';
    background-color: ${({ theme }) => theme.colors.secondary};
    position: absolute;
    left: 0;
    top: 10px;
    width: 20px;
    height: 75px;
    z-index: 2;
  }
`;

export default function QuickSearch({ modalName }) {
  const [isLoading, setIsLoading] = useState(false);
  const [reCaptchaKey, setReCaptchaKey] = useState('');
  const [clearValues, setClearValues] = useState(false);
  const user = useSelector(state => state.user);
  const modal = useSelector(state => state.modal);
  const issuer = useSelector(state => state?.issuer);
  const isVR = issuer?.name === VRPAT;
  const history = useHistory();
  const toast = useContext(ToastCtx);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    setClearValues(false);
    if (!modal) {
      setClearValues(true);
    }
  }, [modal]);

  async function getReCaptchaKey() {
    try {
      setIsLoading(true);
      const response = await common.getReCaptchaKey();
      const { result } = response;

      setReCaptchaKey(result?.secretKey);
    } catch (e) {
      toast.dispatch(messages(e, 'recaptcha'));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!reCaptchaKey && modalName) {
      getReCaptchaKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalName]);

  async function getCards() {
    let response = '';
    try {
      setIsLoading(true);
      response = await cards.getCards();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }

    return response;
  }

  async function handleNominalCard(error) {
    if (error?.messages.find(item => item.code === '-31')) {
      dispatch(closeModal());

      const response = getCards();
      if (response?.length > 0) {
        history.push('/home');
      } else {
        history.push('/validar-cartao');
      }
    }
  }

  function handleError(error) {
    let component;
    if (location.pathname === '/validar-cartao') {
      component = (
        <>
          <strong>Consulta Rápida</strong>
          <p>
            Por favor, para consultar esse cartão realize a etapa de validação
          </p>
        </>
      );
    } else {
      component = (
        <>
          <strong>Consulta Rápida</strong>
          <p>
            Consulta exclusiva para cartões provisórios. Para consultar o saldo
            e extrato desse cartão vá até o painel principal e, se necessário,
            realize o desbloqueio
          </p>
        </>
      );
    }
    toast.dispatch(messages(error, 'quick_search', component));
  }

  async function handleSubmit(formPayload) {
    if (isLoading) {
      return;
    }

    setClearValues(false);
    const { cardNumber } = formPayload;

    const payload = {
      cpf: user?.cpf,
      cardNumber: cardNumber.split(' ').join(''),
    };

    try {
      setIsLoading(true);

      const data = await cards.provisionalCard(payload);

      dispatch(closeModal());
      dispatch(setProvisionalCard(data?.result));

      history.push('/cartao-provisorio');
    } catch (error) {
      handleError(error);

      await handleNominalCard(error);
    } finally {
      setIsLoading(false);
      setClearValues(true);
    }
  }

  return (
    <QuickSearchContainer>
      <ContainerImage>
        {isVR && (
          <Image
            src={GirlSmilingImg}
            alt="Mulher sorridente sentada com as pernas cruzadas e um notebook em seu colo. Ela está olhando para frente e usando um laço na cabeça, camiseta, calça jeans e tênis."
          />
        )}
        <TitleContainer isVR={isVR}>
          <Title isVR={isVR}>Consulte seu saldo</Title>
        </TitleContainer>
        <Description>
          Consulte rapidamente o saldo e extrato do seu cartão provisório{' '}
          {issuer?.company}
        </Description>
      </ContainerImage>
      <FormContainer>
        <Loader isLoading={isLoading}>
          <QuickSearchForm
            clearValues={clearValues}
            onSubmit={event => handleSubmit(event)}
            reCaptchaKey={reCaptchaKey}
          />
        </Loader>
      </FormContainer>
    </QuickSearchContainer>
  );
}

QuickSearch.propTypes = {
  modalName: PropTypes.string,
};
